import { Flex, Text, Link, Button } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { BillingChangeRequestType } from "~/graphql/supergraphEnums";
import { SPACING } from "~/styles/variables";
import { useBillingDate } from "../../contexts/BillingDateContext";
import { ResolutionRenderer } from "../components/ResolutionRenderer";
import { StatusRenderer } from "../components/StatusRenderer";
import * as styles from "./styles_vanilla.css";
import { CaseTypeDisplay } from "./types";
import type { BillingCaseRowItem } from "../types";
import type { ReviewerConclusion } from "./types";

export type CaseDetailsHeaderProps = {
  billingCase: BillingCaseRowItem;
  onClose: () => void;
  reviewerConclusion?: ReviewerConclusion | null;
  isDraft?: boolean;
  hasLocalChanges?: boolean;
  hasPendingCases?: boolean;
};

export const CaseDetailsHeader = ({
  billingCase,
  onClose,
  reviewerConclusion,
  isDraft = false,
  hasLocalChanges = false,
  hasPendingCases = false,
}: CaseDetailsHeaderProps) => {
  const { asOfDateString } = useBillingDate();
  const resolution = reviewerConclusion ?? billingCase.resolution;
  const caseTypeLabel =
    billingCase.requestType === BillingChangeRequestType.FirstCapitalCallDate
      ? CaseTypeDisplay.FirstCapitalCallDate
      : CaseTypeDisplay.ClientInformation;

  return (
    <Flex flexDirection="column" gap={SPACING.SMALL}>
      <Flex justifyContent="space-between" alignItems="flex-start">
        <Flex flexDirection="column" gap={SPACING.X_SMALL}>
          <Text variant="h2">{billingCase.clientName}</Text>
          <Flex alignItems="center" gap={SPACING.SMALL}>
            <Text variant="secondary-md" color={Colors.Neutral600}>
              {caseTypeLabel}
            </Text>
            <Text variant="secondary-md" color={Colors.Neutral600}>
              |
            </Text>
            <Link
              href={`/canopy/billing/client/${billingCase.clientId}?${new URLSearchParams({ asOfDate: asOfDateString }).toString()}`}
              shouldOpenInNewTab
              icon="external-link"
              iconPosition="right"
              iconVariant="outline"
              iconSize="small"
            >
              {`Billing information for ${billingCase.clientName}`}
            </Link>
          </Flex>
          <Flex
            alignItems="flex-start"
            gap={SPACING.X_LARGE}
            className={styles.statusResolutionRow}
          >
            <Flex flexDirection="column" gap={SPACING.X_SMALL}>
              {isDraft ? (
                // Draft drawer header reflects what the user is observing:
                //   - staged local edits → "In progress" (their new submission)
                //   - no local edits + pending CRs exist → "In review" (visible
                //     aggregation belongs to those pending CRs, not the draft)
                //   - no local edits + no pending CRs → "In progress" (empty fresh draft)
                hasLocalChanges || !hasPendingCases ? (
                  <Flex alignItems="center" gap={8}>
                    <Icon
                      type="arrows-rotate"
                      color={Colors.Neutral700}
                      data-testid="draft-status-icon"
                    />
                    <Text color={Colors.Neutral700}>In progress</Text>
                  </Flex>
                ) : (
                  <StatusRenderer status="IN_REVIEW" />
                )
              ) : (
                <StatusRenderer status={billingCase.status} />
              )}
              <Text variant="secondary-sm" color={Colors.Neutral600}>
                Status
              </Text>
            </Flex>
            <Flex flexDirection="column" gap={SPACING.X_SMALL}>
              {isDraft ? (
                <Text>--</Text>
              ) : (
                <ResolutionRenderer
                  resolution={resolution}
                  isReviewerConclusion={!!reviewerConclusion}
                />
              )}
              <Text variant="secondary-sm" color={Colors.Neutral600}>
                Resolution
              </Text>
            </Flex>
          </Flex>
        </Flex>
        <Flex gap={SPACING.X_SMALL} alignItems="center">
          <Button
            className={styles.iconButton}
            onClick={onClose}
            aria-label="Close"
            icon="times"
            variant="neutral"
          />
        </Flex>
      </Flex>
    </Flex>
  );
};
